/* eslint-disable complexity */
import {
  InstructionComponentType,
  AttachmentInstructionType,
  InstructionDiffStatus,
} from '@scale/shared/defaults';
import _ from 'lodash';
import { InstructionsComponentJson } from '@scale/shared/projectInstruction/types';
import { iconPropsByType, TOOL_TUTORIALS, waitSeconds } from './constants';
import { ComponentJson, NormalizedComponentProps } from './types';

export const flattenWithPath = (nodes: any[], path: Array<any>, parentId?: string) => {
  const output: Array<any> = [];
  nodes.forEach((node: any, index: number) => {
    const { nodeType, nodeName, nodeId, content, samples, description, title, components } = node;
    const id = parentId ?? nodeId;

    if (nodeType === InstructionComponentType.Group) {
      const newPath = [...path, index];
      const item = { title, nodeId: id, path: [...path, index] };
      output.push(item);
      output.push(...flattenWithPath(node.components, [...newPath, 'groups'], id));
    } else if (
      Object.values(InstructionComponentType).includes(nodeType) ||
      nodeType === TOOL_TUTORIALS
    ) {
      const newPath = [...path, nodeType, nodeName];

      const { description, title, examples } = content;
      const item = { description, title, nodeId: id, path: newPath };
      output.push(item);
      if (examples) {
        output.push(...flattenWithPath(examples, [...newPath, 'examples'], id));
      }
      if (components) {
        output.push(...flattenWithPath(components, path, id));
      }
    } else if (samples) {
      const newPath = [...path, index];
      const item = { description, title, nodeId: id, path: [...path, index] };
      output.push(item);
      output.push(...flattenWithPath(samples, [...newPath, 'samples'], id));
    } else {
      const item = {
        description,
        title,
        nodeId: id,
        path: [...path, index],
        linkedTask: node.linkedTask,
        staticAsset: node.staticAsset,
        screenshotUrl: node.screenshotUrl,
        type: node.type,
      };
      output.push(item);
    }
  });

  return output;
};

const isComponentEmpty = (component: InstructionsComponentJson) => {
  if (!component || _.isEmpty(component)) {
    return true;
  }

  if (component.type === InstructionComponentType.Group) {
    return _.isEmpty(component.components);
  }
  // @ts-ignore
  const { description, labels, attributes, examples } = component;
  return !description && _.isEmpty(labels) && _.isEmpty(attributes) && _.isEmpty(examples);
};

const getTitleToId = (components: InstructionsComponentJson[]) => {
  const obj: Record<string, string> = {};
  components.forEach((comp: InstructionsComponentJson) => {
    const { _id, title } = comp;
    obj[title] = _id;
  });

  return obj;
};

export const dataNormalizer = ({
  components,
  isOnboarding,
  isCustomer,
}: {
  components: InstructionsComponentJson[];
  isOnboarding: boolean;
  isCustomer: boolean;
}) => {
  const res: NormalizedComponentProps[] = [];
  const titleToId = getTitleToId(components);

  components.forEach((comp: InstructionsComponentJson) => {
    if (isComponentEmpty(comp)) {
      return;
    }
    const node: any = {};

    const { _id, type, title, isNew, diffStatus } = comp;
    const countdownSeconds =
      isNew ||
      isOnboarding ||
      diffStatus === InstructionDiffStatus.Addition ||
      diffStatus === InstructionDiffStatus.Update
        ? waitSeconds
        : null;

    if (type === InstructionComponentType.RichText) {
      const isWorkflow = title?.toLowerCase()?.includes('workflow');
      node.nodeName = title;
      node.nodeType = type;
      node.nodeId = _id;
      node.iconName = isWorkflow
        ? iconPropsByType.workflow.iconName
        : iconPropsByType[InstructionComponentType.RichText].iconName;
      node.iconColor = isWorkflow
        ? iconPropsByType.workflow.iconColor
        : iconPropsByType[InstructionComponentType.RichText].iconColor;
      node.waitSeconds = countdownSeconds;
      node.content = {
        ...comp,
        diffStatus: isOnboarding ? InstructionDiffStatus.Addition : diffStatus,
        title: _.startCase(title),
      };
    }

    if (type === InstructionComponentType.Label) {
      node.nodeName = comp.label || title;
      node.nodeType = type;
      node.nodeId = _id;
      node.iconName = iconPropsByType[InstructionComponentType.Label].iconName;
      node.iconColor = iconPropsByType[InstructionComponentType.Label].iconColor;
      node.waitSeconds = countdownSeconds;
      node.content = {
        ...comp,
        diffStatus: isOnboarding ? InstructionDiffStatus.Addition : diffStatus,
        title: _.startCase(comp.label || title),
      };
    }

    if (type === InstructionComponentType.Attribute) {
      node.nodeName = comp.attribute || comp.title;
      node.nodeType = type;
      node.nodeId = _id;
      node.iconName = iconPropsByType[InstructionComponentType.Attribute].iconName;
      node.iconColor = iconPropsByType[InstructionComponentType.Attribute].iconColor;
      node.waitSeconds = countdownSeconds;
      node.content = {
        ...comp,
        diffStatus: isOnboarding ? InstructionDiffStatus.Addition : diffStatus,
        title: _.startCase(comp.attribute || title),
      };
    }

    if (type === InstructionComponentType.Field) {
      node.nodeName = title;
      node.nodeType = type;
      node.nodeId = _id;
      node.iconName = iconPropsByType[InstructionComponentType.Field].iconName;
      node.iconColor = iconPropsByType[InstructionComponentType.Field].iconColor;
      node.waitSeconds = countdownSeconds;
      node.content = {
        ...comp,
        diffStatus: isOnboarding ? InstructionDiffStatus.Addition : diffStatus,

        title: _.startCase(title),
      };
    }

    if (type === InstructionComponentType.Group) {
      node.nodeName = title;
      node.nodeType = type;
      node.nodeId = _id ?? title;
      node.iconName = iconPropsByType[InstructionComponentType.Group].iconName;
      node.iconColor = iconPropsByType[InstructionComponentType.Group].iconColor;
      node.waitSeconds = null;
      node.components = dataNormalizer({
        components: comp.components,
        isOnboarding,
        isCustomer,
      });
      node.content = {
        ...comp,
        diffStatus: isOnboarding ? InstructionDiffStatus.Addition : diffStatus,
        components: node.components,
        title: _.startCase(title),
      };
    }

    if (type === InstructionComponentType.Rule) {
      node.nodeName = title;
      node.nodeId = _id;
      node.nodeType = type;
      node.iconName = iconPropsByType[InstructionComponentType.Rule].iconName;
      node.iconColor = iconPropsByType[InstructionComponentType.Rule].iconColor;
      node.waitSeconds = countdownSeconds;
      node.content = {
        ...comp,
        diffStatus: isOnboarding ? InstructionDiffStatus.Addition : diffStatus,
        title: _.startCase(title),
        labels: comp?.labels?.map((label: string) => ({ title: label, id: titleToId[label] })),
        attributes: comp?.attributes?.map((attribute: string) => ({
          title: attribute,
          id: titleToId[attribute],
        })),
      };
    }

    if (type === InstructionComponentType.Attachment) {
      node.nodeName = title;
      node.nodeId = _id;
      node.nodeType = type;
      if (comp.attachmentType === AttachmentInstructionType.Iframe) {
        node.iconName = iconPropsByType[AttachmentInstructionType.Iframe].iconName;
        node.iconColor = iconPropsByType[AttachmentInstructionType.Iframe].iconColor;
      }
      if (comp.attachmentType === AttachmentInstructionType.Image) {
        node.iconName = iconPropsByType[AttachmentInstructionType.Image].iconName;
        node.iconColor = iconPropsByType[AttachmentInstructionType.Image].iconColor;
      }
      if (comp.attachmentType === AttachmentInstructionType.Pdf) {
        node.iconName = iconPropsByType[AttachmentInstructionType.Pdf].iconName;
        node.iconColor = iconPropsByType[AttachmentInstructionType.Pdf].iconColor;
      }
      node.waitSeconds = countdownSeconds;
      node.content = {
        ...comp,
        diffStatus: isOnboarding ? InstructionDiffStatus.Addition : diffStatus,
        title: _.startCase(title),
      };
    }

    res.push(node);
  });

  return res;
};

export const getEachSubComponentDataFromId = (
  components: NormalizedComponentProps[],
  subAttrIds: string[],
): ComponentJson[] => {
  // todo memoize
  const subAttrData: ComponentJson[] = [];
  components.map((c: NormalizedComponentProps) => {
    if (c.content?._id && subAttrIds.includes(c.content?._id)) {
      subAttrData.push(c.content);
    }
  });
  return subAttrData;
};
